import { useSessionStore } from "@/core/stores/session.store";

/**
 * Gardes par rôle — guide §3.3.
 *
 * Les rôles de realm `flashpay-*` sont portés par le token
 * (`realm_access.roles`) et synchronisés dans `session.store.userInfo` par
 * les deux modes d'auth (sso : tokenParsed ; password : claims décodés).
 *
 * Règle d'usage : MASQUER les écrans/actions interdits (décision plan
 * n°3 — boutons d'orchestration visibles uniquement aux rôles habilités),
 * le backend reste l'autorité finale (403).
 */

/** Rôles de realm définis par le guide §3.3 (v4.1 — 7 rôles). */
export const FLASHPAY_ROLES = {
  /** Administrateur de la plateforme — tout accès, y compris suppressions logiques et configuration système. */
  SUPERADMIN: "flashpay-superadmin",
  /** Back-office — revue KYC, tarification, plan de comptes, approbations, marchands. */
  ADMIN: "flashpay-admin",
  /** Comptes techniques inter-services — comptabilisation, acquittements, notifications, ventes. */
  SERVICE: "flashpay-service",
  /** Agents de terrain FlashPay — recherche d'identité, enrôlement KYC assisté. */
  AGENTS: "flashpay-agents",
  /** Marchand — consultation de ses ventes et lancement de ses paies de masse (v4.1). */
  MERCHANT: "flashpay-merchant",
  /** Agent de vente d'un marchand — son profil et ses ventes uniquement, aucune gestion du compte marchand. */
  MERCHANT_SALES_AGENT: "flashpay-merchant-sales-agent",
  /** Compte client final (v4.1 — app mobile self-service). Hors scope back-office : jamais autorisé à atteindre `/app/*`. */
  CUSTOMER: "flashpay-customer",
} as const;

export type FlashpayRole = (typeof FLASHPAY_ROLES)[keyof typeof FLASHPAY_ROLES];

/**
 * Sentinelle stable : un `[]` littéral dans le sélecteur Zustand serait une
 * nouvelle référence à chaque `getSnapshot` → boucle React
 * `useSyncExternalStore` (« Maximum update depth exceeded », error #185)
 * dès que `userInfo` / `realm_access.roles` est absent.
 */
const EMPTY_REALM_ROLES: readonly string[] = [];

const readRealmRoles = (
  userInfo: Record<string, unknown> | null | undefined
): readonly string[] => {
  const realmAccess = userInfo?.realm_access as { roles?: unknown } | undefined;
  return Array.isArray(realmAccess?.roles)
    ? (realmAccess.roles as string[])
    : EMPTY_REALM_ROLES;
};

/** Rôles de realm de l'utilisateur courant (depuis les claims du token). */
export const getRealmRoles = (): string[] => [
  ...readRealmRoles(useSessionStore.getState().userInfo),
];

/** Hook réactif : rôles de realm courants (re-rend au login/logout). */
export const useRealmRoles = (): readonly string[] =>
  useSessionStore((s) => readRealmRoles(s.userInfo));

export const hasRole = (role: FlashpayRole | string): boolean =>
  getRealmRoles().includes(role);

export const hasAnyRole = (roles: readonly (FlashpayRole | string)[]): boolean =>
  roles.some((role) => getRealmRoles().includes(role));

/**
 * Rôles « back-office strict » : revue KYC, tarification, plan de comptes,
 * approbations, marchands, outbox, administration. Allowlist explicite (décision
 * 42 du plan 2026-10-28) — n'inclut PAS MERCHANT / MERCHANT_SALES_AGENT /
 * CUSTOMER par mégarde.
 */
export const BACKOFFICE_ROLES: readonly FlashpayRole[] = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
  FLASHPAY_ROLES.AGENTS,
] as const;

/**
 * Rôles autorisés à entrer dans l'application (`/app/*`) : BO strict + marchand
 * + agent de vente marchand. Le rôle CUSTOMER (compte client final self-service)
 * est volontairement exclu — il est rejeté dès le login / la restauration de
 * session (décision 42 du plan 2026-10-28).
 */
export const APP_ACCESS_ROLES: readonly FlashpayRole[] = [
  ...BACKOFFICE_ROLES,
  FLASHPAY_ROLES.MERCHANT,
  FLASHPAY_ROLES.MERCHANT_SALES_AGENT,
] as const;

/** Vrai si l'utilisateur courant a au moins un rôle back-office strict. */
export const hasBackofficeAccess = (): boolean => hasAnyRole(BACKOFFICE_ROLES);

/**
 * Vrai si l'utilisateur courant peut entrer dans l'application (BO + marchand).
 * Les flux d'authentification s'appuient dessus pour rejeter les comptes
 * self-service (`flashpay-customer`, `default-roles-flash-pay`).
 */
export const hasAppAccess = (): boolean => hasAnyRole(APP_ACCESS_ROLES);

/**
 * Rôles autorisés à utiliser la paie (`/app/payroll` et `/app/payroll/runs`) :
 * le marchand titulaire uniquement. Superadmin, admin et compte technique
 * n'ouvrent pas ces écrans.
 */
export const PAYROLL_MERCHANT_ROLES: readonly FlashpayRole[] = [
  FLASHPAY_ROLES.MERCHANT,
] as const;
